/**
 * Packer Schematic helper functions.
 * Pure functions with no Angular dependencies - easily testable.
 */

import { Packer, Packers } from 'src/app/wellbore-inputs/models/packers.model';
import { saveAs } from 'file-saver-es';
import { convertSvgToPngBlob } from 'src/app/shared/helpers/schematic-export.helper';

/**
 * Format HTTP error into user-friendly message.
 * Handles various error response formats from the backend.
 */
export function formatErrorMessage(error: any): string {
  let errorMsg = 'Failed to load packer schematic';
  
  // Backend returns errors as string array in error.error
  if (error?.error) {
    if (Array.isArray(error.error)) {
      errorMsg = error.error.join('\n');
    } else if (typeof error.error === 'string') {
      errorMsg = error.error;
    }
  } else if (error?.message) {
    errorMsg = error.message;
  }
  
  // Special case for "No packers found" - more user-friendly message
  return errorMsg.includes('No packers found') 
    ? 'No packers available in this tubular string'
    : errorMsg;
}

/**
 * Determine if expansion joint focus toggle should be shown.
 * Only shown for true telescoping expansion joints (EJ enabled and depth set away from packer).
 */
export function shouldShowExpansionJointToggle(
  packersData: Packers | null,
  packerIndex: number
): boolean {
  if (!packersData?.packers || packerIndex < 0 || packerIndex >= packersData.packers.length) {
    return false;
  }

  const selectedPacker = packersData.packers[packerIndex] as Packer | undefined;
  
  return selectedPacker?.hasExpansionJoint === true &&
         selectedPacker?.advancedExpansionJoint?.isDepthNotAtPacker === true;
}

/**
 * Download SVG schematic as PNG file using shared export helper.
 * Uses the shared convertSvgToPngBlob function to avoid code duplication.
 */
export async function downloadSvgAsPng(sourceSvg: SVGSVGElement, fileName: string): Promise<void> {
  try {
    const blob = await convertSvgToPngBlob(sourceSvg);
    if (blob) {
      saveAs(blob, fileName);
    } else {
      console.error('Failed to convert SVG to PNG blob.');
    }
  } catch (err) {
    console.error('Error exporting SVG as PNG:', err);
  }
}
